<template>
  <div class="brand-show-page">
    <!-- Brand Header -->
    <div class="header-container">
      <div class="brand-header" :style="brand.banner_desktop_url ? { backgroundImage: `url(${brand.banner_desktop_url})` } : {}">
        <div class="header-content">
          <div class="brand-logo-box">
            <img v-if="brand.logo_url" :src="brand.logo_url" :alt="brand.name" class="brand-logo-img" />
            <div v-else class="brand-initial">{{ brand.name.charAt(0) }}</div>
          </div>
          <div class="brand-info">
            <h1 class="brand-title">{{ brand.name }}</h1>
            <p v-if="brand.description" class="brand-desc">{{ brand.description }}</p>
            <div class="brand-meta">
              <span class="meta-tag"><i class="fas fa-box"></i> {{ products.total || 0 }} Products</span>
              <span v-if="categories.length > 0" class="meta-tag"><i class="fas fa-th-large"></i> {{ categories.length }} Categories</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Products Section -->
    <div class="products-container">
      <div class="products-layout">
        <!-- Filters Sidebar -->
        <div class="filters-sidebar">
          <div class="filters-card">
            <h3 class="filters-title">Filters</h3>

            <!-- Category Filter -->
            <div v-if="categories.length > 0" class="filter-group">
              <label>Category</label>
              <select v-model="filters.category_id" @change="applyFilters">
                <option value="">All Categories</option>
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ category.name }}
                </option>
              </select>
            </div>

            <!-- Sort By -->
            <div class="filter-group">
              <label>Sort By</label>
              <select v-model="filters.sort" @change="applyFilters">
                <option value="">Default</option>
                <option value="price_low">Price: Low to High</option>
                <option value="price_high">Price: High to Low</option>
                <option value="newest">Newest First</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>

            <!-- Clear Filters -->
            <button v-if="hasActiveFilters" class="btn-clear-filters" @click="clearFilters">
              <i class="fas fa-times"></i> Clear Filters
            </button>
          </div>
        </div>

        <!-- Products Grid -->
        <div class="products-main">
          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i>
            <p>Loading products...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="displayProducts.length === 0" class="empty-state">
            <i class="fas fa-box-open"></i>
            <h2>No Products Found</h2>
            <p>Try adjusting your filters</p>
          </div>

          <!-- Products -->
          <div v-else>
            <div class="products-grid">
              <product-card v-for="product in displayProducts" :key="product.id" :product="product" />
            </div>

            <!-- Pagination -->
            <div v-if="products.last_page > 1" class="pagination-wrapper">
              <a v-if="products.prev_page_url" :href="products.prev_page_url" class="pagination-btn">
                <i class="fas fa-chevron-left"></i> Previous
              </a>
              <span class="page-info">Page {{ products.current_page }} of {{ products.last_page }}</span>
              <a v-if="products.next_page_url" :href="products.next_page_url" class="pagination-btn">
                Next <i class="fas fa-chevron-right"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ProductCard from '../../components/home/ProductCard.vue'

export default {
  name: 'BrandShow',
  components: {
    ProductCard
  },
  props: {
    brand: {
      type: Object,
      required: true
    },
    products: {
      type: Object,
      required: true
    },
    categories: {
      type: Array,
      default: () => []
    },
    priceRange: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      filters: {
        category_id: '',
        sort: ''
      },
      displayProducts: []
    }
  },
  computed: {
    hasActiveFilters() {
      return this.filters.category_id || this.filters.sort
    }
  },
  mounted() {
    this.displayProducts = this.products.data || []
    this.initFiltersFromUrl()
  },
  methods: {
    initFiltersFromUrl() {
      const params = new URLSearchParams(window.location.search)
      this.filters.category_id = params.get('category_id') || ''
      this.filters.sort = params.get('sort') || ''
    },
    applyFilters() {
      this.loading = true
      const params = new URLSearchParams()
      if (this.filters.category_id) params.append('category_id', this.filters.category_id)
      if (this.filters.sort) params.append('sort', this.filters.sort)
      window.location.href = `${window.location.pathname}?${params.toString()}`
    },
    clearFilters() {
      this.filters = { category_id: '', sort: '' }
      window.location.href = window.location.pathname
    }
  },
  watch: {
    products: {
      handler(newProducts) {
        this.displayProducts = newProducts.data || []
        this.loading = false
      },
      deep: true
    }
  }
}
</script>

<style scoped>
.brand-show-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 3rem;
}

.header-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem 1rem 0;
}

.brand-header {
  padding: 1.5rem;
  border-radius: 12px;
  background-size: cover;
  background-position: center;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.brand-logo-box {
  width: 80px;
  height: 80px;
  background: #fff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 8px;
}

.brand-logo-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.brand-initial {
  font-size: 2rem;
  font-weight: 700;
  color: #FD6600;
}

.brand-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 0.5rem;
}

.brand-desc {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  margin: 0 0 0.75rem;
  line-height: 1.5;
}

.brand-meta {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.meta-tag {
  background: rgba(255, 255, 255, 0.15);
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.meta-tag i {
  color: #FD6600;
}

.products-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 1.5rem 1rem 0;
}

.products-layout {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 1.5rem;
}

.filters-card {
  background: #fff;
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid #e5e5e5;
}

.filters-title {
  font-size: 1rem;
  font-weight: 700;
  color: #313131;
  margin: 0 0 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #FD6600;
}

.filter-group {
  margin-bottom: 1rem;
}

.filter-group label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #313131;
  margin-bottom: 0.4rem;
}

.filter-group select {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #313131;
  background: #fff;
}

.btn-clear-filters {
  width: 100%;
  padding: 0.5rem;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.8rem;
  color: #666;
  cursor: pointer;
}

.products-main .products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #666;
}

.loading-state i,
.empty-state i {
  font-size: 2rem;
  color: #FD6600;
  margin-bottom: 0.75rem;
  display: block;
}

.empty-state h2 {
  font-size: 1rem;
  color: #313131;
  margin-bottom: 0.5rem;
}

.pagination-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #313131;
  text-decoration: none;
  font-size: 0.85rem;
}

.pagination-btn:hover {
  background: #FD6600;
  border-color: #FD6600;
  color: #fff;
}

.page-info {
  font-size: 0.85rem;
  color: #666;
}

/* Tablet */
@media (max-width: 900px) {
  .products-layout {
    grid-template-columns: 1fr;
  }

  .filters-card {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: flex-end;
  }

  .filters-title {
    width: 100%;
    margin-bottom: 0.5rem;
  }

  .filter-group {
    flex: 1;
    min-width: 120px;
    margin-bottom: 0;
  }

  .btn-clear-filters {
    width: auto;
    padding: 0.5rem 1rem;
  }
}

/* Mobile */
@media (max-width: 768px) {
  .header-container {
    padding: 0.75rem 0.75rem 0;
  }

  .brand-header {
    padding: 1rem;
    border-radius: 10px;
  }

  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }

  .brand-logo-box {
    width: 60px;
    height: 60px;
  }

  .brand-initial {
    font-size: 1.5rem;
  }

  .brand-title {
    font-size: 1.2rem;
  }

  .brand-desc {
    font-size: 0.8rem;
  }

  .brand-meta {
    justify-content: center;
  }

  .meta-tag {
    font-size: 0.7rem;
    padding: 0.3rem 0.6rem;
  }

  .products-container {
    padding: 1rem 0.75rem 0;
  }

  .filters-card {
    flex-direction: column;
    padding: 0.75rem;
  }

  .filters-title {
    font-size: 0.9rem;
  }

  .filter-group {
    width: 100%;
  }

  .filter-group label {
    font-size: 0.8rem;
  }

  .filter-group select {
    font-size: 0.8rem;
    padding: 0.4rem;
  }

  .btn-clear-filters {
    width: 100%;
    font-size: 0.75rem;
  }

  .products-main .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
  }

  .pagination-btn {
    font-size: 0.75rem;
    padding: 0.4rem 0.75rem;
  }

  .page-info {
    font-size: 0.75rem;
  }
}

/* Small Mobile */
@media (max-width: 480px) {
  .brand-logo-box {
    width: 50px;
    height: 50px;
  }

  .brand-initial {
    font-size: 1.25rem;
  }

  .brand-title {
    font-size: 1.1rem;
  }

  .brand-desc {
    font-size: 0.75rem;
  }

  .meta-tag {
    font-size: 0.65rem;
    padding: 0.25rem 0.5rem;
  }
}
</style>
